// [PHASE B / #017 — B3-6]: Build a fully-shaped shop item from a raw
//   itemKey + rarity. Single source of truth for the
//   "lowercase underscored key → shop item with normalized display name"
//   transformation, replacing the inline `name: randomItemName` (raw key)
//   construction at App.jsx:6472 + :6528.
//
// Why a shared helper (Rule 22 — no inline test fabrications):
//   - Two production sites (Simple-mode pool + rarity-pool fallback) used
//     to inline the same construction, polluting `item.name` with the
//     lowercase underscored itemKey. Phase B / #010 fixed the renderer's
//     fallback chain (`item.name → formatDisplayName → underscore swap`)
//     but the renderer's first fallback is `item.name`, so polluting
//     `item.name` defeated the rendering fix.
//   - Both sites now import this helper. Tests exercise the same helper.
//   - shopGenerator.coverage.test.js's inline `resolveRoll` will be
//     migrated in a follow-up commit (it covers the rollFromTable / DMG
//     d100 path, which is structurally different but suffers the same
//     class of name-pollution bug — the test currently passes because
//     entry.name is set on DMG entries).
//
// Display-name fallback chain (matches formatRecentFavoriteName):
//   1. def.entry.name (BASE_ITEMS row's explicit display name, if set)
//   2. EquipmentResolver.formatDisplayName(itemKey) (special-cases dict
//      + Title Case fallback)
//   3. itemKey.replace(/_/g, ' ') (last resort — never reached if
//      formatDisplayName returns something, which it does for any
//      non-empty string per the helper contract)

import { getItemDefinition } from '../data/gameData';
import { EquipmentResolver } from './equipmentResolver';

/**
 * Build a fully-shaped shop item from a raw BASE_ITEMS itemKey + rarity.
 *
 * @param {string} itemKey - Raw BASE_ITEMS key (e.g. 'leather', 'bag_of_holding').
 * @param {string} rarity - Rarity tag for the generated shop entry.
 * @returns {object|null} Shop item with normalized name + carried-through
 *   BASE_ITEMS row, or null if itemKey doesn't resolve.
 */
export function buildShopItem(itemKey, rarity) {
  if (typeof itemKey !== 'string' || !itemKey) return null;
  const def = getItemDefinition(itemKey);
  if (!def) return null;

  const name =
    def.entry.name ||
    EquipmentResolver.formatDisplayName(itemKey) ||
    itemKey.replace(/_/g, ' ');

  return {
    ...def.entry,
    name,
    category: def.category,
    rarity,
    baseValue: def.entry.baseValue || 1,
  };
}
